import { useState, useCallback, useEffect, useMemo } from "react";
import { Box, Fab, Button, Typography, IconButton } from "@mui/material";
import {
  Add,
  Check,
  Close,
  AccessTime,
  Person,
  Delete,
  Comment,
} from "@mui/icons-material";
import WeeklyCalendar from "../Components/Calendar/WeeklyCalendar";
import CalendarHeader from "../Components/Calendar/CalendarHeader";
import {
  ICalendarEvent,
  getWeekStart,
} from "../Components/Calendar/CalendarUtils";
import GenericModal from "../Components/GenericModal";
import HoursForm from "../Components/Hours/HoursForm";
import { Vocabulary } from "../Utils/Vocabulary";
import { EWidths, HourStatusEnum } from "../Utils/Enums";
import { endpoints, localUrlEnum } from "../Utils/UrlEnum";
import { getData } from "../Services/getData";
import { updateData } from "../Services/updateData";
import { deleteData } from "../Services/deleteData";
import { DRIVING_LESSON_NOTIFICATION_TYPES } from "../Utils/Constants";
import { postData } from "../Services/postData";

import styles from "../Components/Exams/ExamsCalendar.module.css";
import genericStyles from "../Styles/genericStyles.module.css";
import eventCardStyles from "./DrivingHoursEventCard.module.css";
import { AxiosResponse } from "axios";
import { getFullName } from "../Utils/Utils";
import { ProductsModel } from "../Utils/Models";
import withRole from "../Hooks/withRole";
import { useNavigate } from "react-router-dom";

function DrivingHoursCalendar(props: any) {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<{
    date: Date;
    timeSlot?: string;
  } | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [events, setEvents] = useState<ICalendarEvent[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [products, setProducts] = useState<ProductsModel[]>([]);

  // Generate time slots from 8:00 to 20:00
  const timeSlots = Array.from(
    { length: 13 },
    (_, i) => `${String(i + 8).padStart(2, "0")}:00`
  );

  /**
   * Check if instructor has permission to access this page
   * Redirect to page not found if canScheduleHours is not true
   */
  useEffect(() => {
    if (props.isInstructor) {
      const canScheduleHours = localStorage.getItem("canScheduleHours");
      if (canScheduleHours !== "true") {
        navigate(localUrlEnum.pageNotFound);
      }
    }
  }, [props.isInstructor, navigate]);

  /**
   * Fetch driving hours from backend
   */
  const fetchDrivingHours = useCallback(async () => {
    try {
      // Calculate week start and end dates
      const weekStart = getWeekStart(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      // Format dates for API
      const startDate = weekStart.toISOString();
      const endDate = weekEnd.toISOString();

      // Get user role and userId to check if instructor
      const userId = localStorage.getItem("userId");
      const userRoles = JSON.parse(localStorage.getItem("userRoles") || "[]");
      const isInstructor = userRoles.some(
        (role: any) => role.name === "Instructor"
      );

      // Build API URL with instructorUserId param if user is instructor
      let apiUrl = `${endpoints.hours.main}?startDate=${startDate}&endDate=${endDate}`;
      if (isInstructor && userId) {
        apiUrl += `&instructorUserId=${userId}`;
      }

      const response = await getData(apiUrl);
      if (response?.data) {
        // Transform backend data to calendar events
        const calendarEvents: ICalendarEvent[] = response.data;
        setEvents(calendarEvents);
      }
    } catch (error) {
      console.error("Error fetching driving hours:", error);
    }
  }, [currentDate]);

  useEffect(() => {
    getInstructors();
    fetchProducts();
  }, []);

  /**
   * Fetches the list of instructors from the backend and stores the result in the state.
   * Filters to only include instructors with canScheduleHours: true
   */
  const getInstructors = () => {
    getData(
      `${endpoints.instructor.user}/paginated?page=0&perPage=100000`
    ).then((res: AxiosResponse) => {
      if (!res?.data) return;
      // Filter instructors to only include those with canScheduleHours: true
      const filteredInstructors = res.data?.results.filter(
        (instructor: any) => instructor.canScheduleHours === true
      );
      setInstructors(filteredInstructors);
    });
  };

  /**
   * Fetches the list of products from the backend
   */
  const fetchProducts = async () => {
    try {
      const res = (await getData(`${endpoints.product.main}`)) as AxiosResponse;
      if (res?.data) {
        setProducts(res.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  /**
   * Fetch driving hours on mount and when date changes
   */
  useEffect(() => {
    fetchDrivingHours();
  }, [fetchDrivingHours]);

  /**
   * Handle date selection from calendar
   */
  const handleDateSelect = useCallback(
    (date: Date, _events: ICalendarEvent[], timeSlot?: string) => {
      setSelectedSlot({ date, timeSlot });
      setOpenModal(true);
    },
    []
  );

  /**
   * Handle navigation (week)
   */
  const handleNavigate = useCallback(
    (direction: "prev" | "next") => {
      const weekStart = getWeekStart(currentDate);
      weekStart.setDate(weekStart.getDate() + (direction === "next" ? 7 : -7));
      const newDate = new Date(weekStart.getTime());
      setCurrentDate(newDate);
    },
    [currentDate]
  );

  /**
   * Handle navigate to current week
   */
  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  /**
   * Close modal handler
   */
  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedSlot(null);
  }, []);

  /**
   * Check if the selected slot can be edited (endDate hasn't passed)
   */
  const canEditSlot = useMemo(() => {
    if (!selectedSlot) return true;
    
    // Check if selectedSlot has endDate property (it's an existing event)
    const slot = selectedSlot as any;
    const endDate = slot.endDate ? new Date(slot.endDate) : null;
    if (!endDate) return true;
    
    const now = new Date();
    return now < endDate;
  }, [selectedSlot]);

  /**
   * Handle form submit success - refresh events
   */
  const handleFormSuccess = useCallback(() => {
    fetchDrivingHours();
    handleCloseModal();
  }, [fetchDrivingHours, handleCloseModal]);

  /**
   * Send driving lesson notification
   */
  const sendDrivingLessonNotification = async (
    hourId: string,
    clientId: string,
    type: string
  ) => {
    await postData(endpoints.notifications.sendDrivingLesson, {
      hourId,
      clientId,
      type,
    });
  };

  /**
   * Handle approve driving hour
   */
  const handleApprove = useCallback(
    async (eventId: string, clientId: string) => {
      try {
        await updateData(`${endpoints.hours.main}/${eventId}`, {
          status: HourStatusEnum.APPROVED,
        }).then(async () => {
          // send notification
          await sendDrivingLessonNotification(
            eventId,
            clientId,
            DRIVING_LESSON_NOTIFICATION_TYPES.LESSON_APPROVED_BY_INSTRUCTOR
          );
        });

        fetchDrivingHours();
      } catch (error) {
        console.error("Error approving driving hour:", error);
      }
    },
    [fetchDrivingHours]
  );

  /**
   * Handle decline driving hour - becomes available again
   */
  const handleDecline = useCallback(
    async (eventId: string, clientId: string) => {
      try {
        await updateData(`${endpoints.hours.main}/${eventId}`, {
          status: HourStatusEnum.AVAILABLE,
          clientId: null,
          comments: null,
          orderId: null,
        }).then(async () => {
          // send notification
          await sendDrivingLessonNotification(
            eventId,
            clientId,
            DRIVING_LESSON_NOTIFICATION_TYPES.LESSON_REJECTED_BY_INSTRUCTOR
          );
        });
        fetchDrivingHours();
      } catch (error) {
        console.error("Error declining driving hour:", error);
      }
    },
    [fetchDrivingHours]
  );

  /**
   * Handle delete driving hour
   */
  const handleDelete = useCallback(
    async (eventId: string) => {
      try {
        await deleteData(`${endpoints.hours.main}/${eventId}`);
        fetchDrivingHours();
      } catch (error) {
        console.error("Error deleting driving hour:", error);
      }
    },
    [fetchDrivingHours]
  );

  const transformSlot = (slot?: any) => {
    if (!slot) return {};

    // If slot is already an event with startDate/endDate
    if (slot.startDate && slot.endDate) {
      return {
        ...slot,
        date: new Date(slot.startDate),
        startHour: new Date(slot.startDate),
        endHour: new Date(slot.endDate),
      };
    }

    // Otherwise it's a time slot selection
    const baseDate = new Date(slot.date);

    let startHour: Date | undefined;
    if (slot.timeSlot) {
      const [hours, minutes] = slot.timeSlot.split(":");
      baseDate.setHours(Number(hours), Number(minutes), 0, 0);
      startHour = new Date(baseDate);
    }

    let endHour: Date | undefined;
    if (startHour) {
      endHour = new Date(startHour);
      endHour.setMinutes(endHour.getMinutes() + 90);
    }

    return {
      date: baseDate,
      ...(startHour && { startHour }),
      ...(endHour && { endHour }),
      status: HourStatusEnum.AVAILABLE,
    };
  };

  return (
    <Box className={styles.modernContainer}>
      <CalendarHeader
        title={Vocabulary.scheduleDrivingHours}
        currentDate={currentDate}
        onNavigate={handleNavigate}
        onToday={handleToday}
      />

      <WeeklyCalendar
        events={events}
        onDateSelect={handleDateSelect}
        initialDate={currentDate}
        currentDate={currentDate}
        className={styles.examCalendar}
        timeSlots={timeSlots}
        renderEventCard={(event) => {
          const isPending =
            event.clientId && event.status === HourStatusEnum.PENDING;
          const isConfirmed =
            event.clientId && event.status === HourStatusEnum.APPROVED;
          const isAvailable = !event.clientId;

          // Check if available entry can be deleted (before endDate passes)
          const now = new Date();
          const endDate = event.endDate ? new Date(event.endDate) : null;
          const canDeleteAvailable = isAvailable && endDate && now < endDate;

          const cardClass = isPending
            ? eventCardStyles.pendingCard
            : isConfirmed
            ? eventCardStyles.confirmedCard
            : isAvailable
            ? eventCardStyles.availableCard
            : "";
          const clientLabel = isAvailable
            ? Vocabulary.available
            : getFullName(event.clientId);

          return (
            <div className={`${eventCardStyles.eventCard} ${cardClass}`}>
              {/* Header with client/status and delete button */}
              <div className={eventCardStyles.cardHeader}>
                <Typography className={eventCardStyles.clientName}>
                  {clientLabel}
                </Typography>
                {canDeleteAvailable && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(event._id);
                    }}
                    className={eventCardStyles.deleteButton}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </div>

              {/* Time and Instructor info */}
              <div className={eventCardStyles.infoSection}>
                {event.startDate && event.endDate && (
                  <div className={eventCardStyles.infoRow}>
                    <AccessTime />
                    <span className={eventCardStyles.infoText}>
                      {new Date(event.startDate).toLocaleTimeString("ro-RO", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      -{" "}
                      {new Date(event.endDate).toLocaleTimeString("ro-RO", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                )}
                {event.instructorId && (
                  <div className={eventCardStyles.infoRow}>
                    <Person />
                    <span className={eventCardStyles.infoText}>
                      {getFullName(event.instructorId?.user_id)}
                    </span>
                  </div>
                )}
                {event.productIds && event.productIds.length > 0 && (
                  <div className={eventCardStyles.infoRow}>
                    <span className={eventCardStyles.infoText}>
                      {event.productIds
                        .map((product: any) => product.name)
                        .join(", ")}
                    </span>
                  </div>
                )}
                {event.comments && (
                  <div className={eventCardStyles.infoRow}>
                    <Comment sx={{ fontSize: 14 }} />
                    <span className={eventCardStyles.infoText}>
                      {event.comments}
                    </span>
                  </div>
                )}
              </div>

              {/* Status badges and actions */}
              {isPending && (
                <div className={eventCardStyles.actionsSection}>
                  <p className={eventCardStyles.approvalText}>
                    {Vocabulary.needsApproval}
                  </p>
                  <div className={eventCardStyles.buttonGroup}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(event._id, event.clientId?._id);
                      }}
                      className={eventCardStyles.approveButton}
                    >
                      <Check sx={{ fontSize: "14px" }} />
                      {Vocabulary.approve}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDecline(event._id, event.clientId?._id);
                      }}
                      className={eventCardStyles.declineButton}
                    >
                      <Close sx={{ fontSize: "14px" }} />
                      {Vocabulary.decline}
                    </button>
                  </div>
                </div>
              )}

              {isConfirmed && (
                <div
                  className={
                    eventCardStyles.statusBadge +
                    " " +
                    eventCardStyles.confirmedBadge
                  }
                >
                  <Check sx={{ fontSize: "12px" }} />
                  {Vocabulary.confirmed}
                </div>
              )}
            </div>
          );
        }}
        onEventClick={(event: any) => {
          if (event.clientId) return; //only editable if wasn't booked
          setOpenModal(true);
          setSelectedSlot(event);
        }}
      />

      <Fab
        className={styles.addFab}
        onClick={() => {
          setSelectedSlot(null);
          setOpenModal(true);
        }}
      >
        <Add />
      </Fab>

      <GenericModal
        open={openModal}
        onClose={handleCloseModal}
        title="Programare Ședință Conducere"
        maxWidth={EWidths.MD}
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              type="button"
              color="error"
              onClick={handleCloseModal}
              variant="outlined"
              fullWidth
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              fullWidth
              type="submit"
              form="hoursForm"
              variant="contained"
              disabled={!canEditSlot}
            >
              {Vocabulary.save}
            </Button>
          </div>
        }
      >
        <Box className={styles.modalContent}>
          <HoursForm
            instructors={instructors}
            onClose={handleFormSuccess}
            defaultValues={selectedSlot ? transformSlot(selectedSlot) : {}}
            products={products}
            showVehicle={false}
            isInstructor={props.isInstructor}
            restrictAccess={!canEditSlot}
          />
        </Box>
      </GenericModal>
    </Box>
  );
}

export default withRole(DrivingHoursCalendar);
